import { useContext } from "react";
import { darkTheme, styled } from "~/ui/style/stitches.config";
import { AuthContext } from "~/utils/useAuth";
import { isInTrial } from "~/api/userApi";
import { trackEvent } from "~/api/analyticsApi";
import { t } from "~/localization/translate";

// Calculate trial time remaining string
const getTrialTimeRemaining = (trialEndsAt: string): string => {
  const oneDay = 24 * 60 * 60 * 1000;
  const oneHour = 60 * 60 * 1000;
  const today = new Date();
  const trialEnd = new Date(trialEndsAt);
  const timeDiff = trialEnd.getTime() - today.getTime();

  const diffHours = Math.floor(timeDiff / oneHour);
  const diffDays = Math.ceil(timeDiff / oneDay);

  // Only show countdown for users with < 15 days left
  if (diffDays >= 15) {
    return "";
  }

  // Less than 1 hour
  if (diffHours === 0 && timeDiff > 0) {
    return t("cta.hour");
  // Less than 9 hours - show hours for urgency
  } else if (diffHours > 0 && diffHours < 9) {
    return diffHours === 1 ? t("cta.hour") : t("cta.hours", { count: diffHours });
  } else if (diffDays === 1) {
    return t("cta.day");
  } else if (diffDays > 0) {
    return t("cta.days", { count: diffDays });
  }
  return "";
};

export const SubscriptionCTA = () => {
  const { user } = useContext(AuthContext);

  const inTrial = user && isInTrial(user);
  const trialTimeLeft = inTrial && user.trialEndsAt
    ? getTrialTimeRemaining(user.trialEndsAt)
    : "";

  // Don't show CTA for extended trial users (no countdown)
  if (inTrial && !trialTimeLeft) {
    return null;
  }

  // Use window.location.href so iOS native app can intercept navigation to show native paywall
  const handleClick = () => {
    trackEvent("/guidebook/pressed_paywall_cta");
    window.location.href = user ? "/plans" : "/signup";
  };

  return (
    <SubscriptionCTAWrapper onClick={handleClick}>
      {inTrial && trialTimeLeft ? (
        <>
          <TrialTimeLabel>{t("cta.leftInTrial", { time: trialTimeLeft })}</TrialTimeLabel>
          {t("cta.keepGuidebook")} <u>{t("cta.subscriptionLink")}</u>
        </>
      ) : user ? (
        <>
          🔒 {t("cta.unlockGuidebook")}<br />
          <u>{t("cta.subscriptionLink")}</u>
        </>
      ) : (
        <><u>{t("cta.tryGuidebook")}</u></>
      )}
    </SubscriptionCTAWrapper>
  );
};

const SubscriptionCTAWrapper = styled("button", {
  width: "100%",
  fontFamily: "worksans",
  fontSize: "13px",
  lineHeight: "1.5",
  color: "$gray700",
  backgroundColor: "#efefef",
  border: "none",
  borderRadius: "14px",
  padding: "20px 24px",
  cursor: "pointer",
  textAlign: "center",
  marginTop: "8px",
  marginBottom: "24px",
  transition: "all 0.2s ease",

  "&:hover": {
    color: "#000",
    backgroundColor: "#e6e6e6",
  },
  [`.${darkTheme.className} &`]: {
    color: "$text",
    backgroundColor: "rgba(15, 15, 15, 0.6)",
    "&:hover": {
      color: "$text",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
    },
  },
});

const TrialTimeLabel = styled("span", {
  display: "block",
  fontSize: "10px",
  fontWeight: "400",
  color: "rgb(0, 0, 0, 0.6)",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
  marginBottom: "6px",
  [`.${darkTheme.className} &`]: {
    color: "$textMuted",
  },
});

